
import Auth from '@/components/page-specific/auth';
import AuthSignupForm from '@/components/page-specific/auth/forms/signup';
import Checkbox from '@/components/form/input/checkbox';
import Gutter from '@/ui-kit/components/gutter';
import Link from '@/ui-kit/components/link';
import Typography from '@/ui-kit/components/typography';
import { signup } from '@/utils/auth';
import redirect from '@/utils/auth/redirect';
import { track } from '@/utils/analytics';
import api from '@/utils/api';
import {
  StyleColorSchemeType,
  StyleSizeType,
  StylePositionType,
  StyleFontWeightType,
} from '@/ui-kit/styles/enums';
import { TypographyType } from '@/ui-kit/components/typography/enums';
import { SubmitButtonTitle, AuthSlotName } from '@/components/page-specific/auth/enums';
import { AUTH_FORM_LEGENDS, AUTH_FORM_TITLES } from '@/components/page-specific/auth/constants';
import { authSignupFormGroup } from '@/components/page-specific/auth/forms/signup/controls';
import { Endpoints } from '@/constants/Api';
import regexps from '@/constants/regexps';
import headMeta from '@/constants/headMeta';
import { FooterLink, FooterLinkProp } from '@/components/page-specific/auth/footer-links/enums';
import { FooterLinkUrl } from '@/components/page-specific/auth/footer-links/constants';
import LocalStorageService from '@/services/LocalStorageService/LocalStorageService';
import { LocalStorageItemKey } from '@/services/LocalStorageService/enums';
import { AuthSignUpTrackEvent } from '@/constants/AnalyticEvents';
import routes from '@/constants/routes';
import { Layout } from '@/enums';
import { UserAttribute } from '@/constants/user';

export default redirect(
  {
    name: 'SignupPage',
    components: {
      Auth,
      Checkbox,
      AuthSignupForm,
      Gutter,
      Link,
      Typography,
    },
    head: {
      title: 'Create an account | Bode Canada',
      meta: [
        { hid: 'description', name: 'description', content: 'Create an account to buy and sell your home with Bode.' },
        { property: 'og:title', content: 'Create an account | Bode Canada' },
        { property: 'og:description', content: 'Create an account to buy and sell your home with Bode.' },
        { property: 'og:url', content: `${headMeta.domain}${routes.signup}` },
        { property: 'og:site_name', content: headMeta.siteName },
        { property: 'og:type', content: headMeta.type },
      ],
    },
    layout: Layout.Basic,
    data() {
      return {
        StylePositionType,
        StyleSizeType,
        StyleColorSchemeType,
        StyleFontWeightType,
        Endpoints,
        routes,
        SubmitButtonTitle,
        TypographyType,
        AuthSlotName,
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        isShowErrorMessage: false,
        errorMessage: null,
        isLoading: false,
      };
    },
    computed: {
      title() {
        const { referrer } = this.$route.query;

        return referrer === routes.proMarketplacePortal
          ? AUTH_FORM_TITLES.SignUpPro
          : AUTH_FORM_TITLES.SignUp;
      },
      legend() {
        const { referrer = '' } = this.$route.query;
        if (referrer === routes.proMarketplacePortal) {
          return AUTH_FORM_LEGENDS.ProMarketplace;
        }

        if (this.isGated) {
          return AUTH_FORM_LEGENDS.Gated;
        }

        return AUTH_FORM_LEGENDS.General;
      },
      isShowSSO() {
        const { referrer = '' } = this.$route.query;
        return referrer !== routes.proMarketplacePortal;
      },

      isGated() {
        const { referrer = '' } = this.$route.query;

        return [routes.soldData, routes.comparables, routes.marketData]
          .includes(referrer);
      },
      isPro() {
        return this.$route.query.referrer === routes.proMarketplacePortal;
      },
      queries() {
        return Object.entries(this.$route.query)
          .reduce((query, [key, value]) => query.concat(`${key}=${value}&`), '?');
      },
      loginRedirect() {
        return `${routes.login}${this.queries}`;
      },
      labelColorScheme() {
        return StyleColorSchemeType.Secondary;
      },
      isFormValid() {
        return authSignupFormGroup.isValid;
      },
      formValue() {
        return authSignupFormGroup.value;
      },
      footerLinks() {
        return [
          {
            [FooterLinkProp.Title]: FooterLink.PrivacyPolicy,
            [FooterLinkProp.Url]: FooterLinkUrl[FooterLink.PrivacyPolicy],
          },
          {
            [FooterLinkProp.Title]: FooterLink.TermsPolicy,
            [FooterLinkProp.Url]: FooterLinkUrl[FooterLink.TermsPolicy],
          },
        ];
      },
    },
    methods: {
      validateForm() {
        authSignupFormGroup.validate();
      },

      validateName(value) {
        return value.replace(regexps.name, '').trim();
      },

      addGrowSurfParticipant(email, firstName, lastName, growSurfId) {
        const participant = {
          email,
          firstName,
          lastName,
          referredBy: growSurfId,
        };

        return api.post(Endpoints.AddGrowSurfParticipant, participant);
      },

      async submit() {
        this.validateForm();

        if (!this.isFormValid) {
          return;
        }

        const {
          email,
          password,
          firstName,
          lastName,
        } = this.formValue;
        const growSurfId = LocalStorageService.fetch(LocalStorageItemKey.GrowSurf);

        try {
          this.isLoading = true;

          track(AuthSignUpTrackEvent.NewUserSubmitted, {
            email,
            firstName,
            lastName,
            referrer: this.$route.query.referrer,
            category: 'New User Signup',
            eventLabel: 'New User Registration - Submitted',
          });

          const user = await signup(email, password, {
            given_name: firstName,
            family_name: lastName,
            'custom:ispro': String(this.isPro),
          });

          if (growSurfId) {
            await this.addGrowSurfParticipant(email, firstName, lastName, growSurfId);
            LocalStorageService.remove(LocalStorageItemKey.GrowSurf);
          }

          this.addUserToLocalStorage(email, password, user.codeDeliveryDetails.Destination);
          this.$router.push({
            name: 'signup-confirm',
            query: {
              ...this.$route.query,
              destination: user.codeDeliveryDetails.Destination,
            },
          });
        } catch (e) {
          // eslint-disable-next-line no-underscore-dangle
          const error = e.name || e.__type;

          track(AuthSignUpTrackEvent.FormSubmissionError, {
            email,
            firstName,
            lastName,
            error,
          });

          this.isShowErrorMessage = true;
          this.errorMessage = error;
          this.isLoading = false;
        }
      },

      addUserToLocalStorage(email, password, destination) {
        LocalStorageService.add(UserAttribute.Email, email);
        LocalStorageService.add(UserAttribute.Password, password);
        LocalStorageService.add(UserAttribute.SignUpDestination, destination);
      },
    },
  },
  routes.search,
);
